import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  CheckSquare, Clock, CheckCircle2, Users, Mail, MessageCircle,
  ExternalLink, Plus, Edit2, Trash2, Globe, ArrowRight, Shield,
  FileText, Lock, CalendarDays, Send, PhoneCall, Loader2, Bell, BellOff,
  RotateCw, AlertCircle, Settings, X
} from 'lucide-react';
import { format, startOfWeek, addDays, isToday } from 'date-fns';
import { it } from 'date-fns/locale';

const WIDGET_META = {
  stats:           { label: 'Statistiche',           icon: CheckSquare,  color: 'text-blue-500' },
  compagnie:       { label: 'Compagnie Assicurative', icon: Globe,       color: 'text-blue-500' },
  interni:         { label: 'Interni Ufficio',        icon: PhoneCall,   color: 'text-blue-500' },
  portali:         { label: 'Portali Esterni',        icon: Lock,        color: 'text-slate-500' },
  task_completati: { label: 'Feed Attività',          icon: CheckCircle2, color: 'text-emerald-500' },
  agenda:          { label: 'Agenda Settimanale',     icon: CalendarDays, color: 'text-indigo-500' },
  chat:            { label: 'Chat Team',              icon: MessageCircle, color: 'text-indigo-500' },
  email:           { label: 'Email Recenti',          icon: Mail,        color: 'text-blue-500' },
  whatsapp:        { label: 'WhatsApp',               icon: MessageCircle, color: 'text-emerald-500' },
  reminder:        { label: 'Promemoria WhatsApp',    icon: Bell,        color: 'text-amber-500' },
  tasks:           { label: 'Task da Completare',     icon: CheckSquare, color: 'text-amber-500' },
  richieste:       { label: 'Richieste Emissione',    icon: FileText,    color: 'text-orange-500' },
};

const CompanyCard = ({ company, isAdmin, onEdit, onDelete }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <a href={company.url} target="_blank" rel="noopener noreferrer" data-testid={`company-${company.id}`}
      className="group relative bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-blue-200 transition-all duration-200 flex flex-col items-center text-center">
      {isAdmin && (
        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          <button onClick={(e) => { e.preventDefault(); onEdit(company); }} className="p-1 rounded bg-white shadow border hover:bg-slate-50"><Edit2 size={11} className="text-slate-500" /></button>
          <button onClick={(e) => { e.preventDefault(); onDelete(company.id); }} className="p-1 rounded bg-white shadow border hover:bg-red-50"><Trash2 size={11} className="text-red-500" /></button>
        </div>
      )}
      {company.logo_url && !imgError ? (
        <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-2 bg-white p-1">
          <img src={company.logo_url} alt={company.nome} className="max-h-full max-w-full object-contain" onError={() => setImgError(true)} />
        </div>
      ) : (
        <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-2 bg-blue-50 text-blue-700 font-bold text-sm">
          {company.nome?.slice(0, 2).toUpperCase()}
        </div>
      )}
      <p className="text-xs font-semibold text-slate-700 group-hover:text-blue-600 transition-colors leading-tight">{company.nome}</p>
      <ExternalLink size={10} className="text-slate-300 group-hover:text-blue-400 mt-1 transition-colors" />
    </a>
  );
};

const PortalCard = ({ portal }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all" data-testid={`portal-${portal.id}`}>
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: portal.colore + '15' }}>
        {portal.icon === 'shield' ? <Shield size={20} style={{ color: portal.colore }} /> : <FileText size={20} style={{ color: portal.colore }} />}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-slate-800">{portal.nome}</h3>
        <p className="text-[11px] text-slate-400 mt-0.5">{portal.descrizione}</p>
        <a href={portal.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-xs font-medium hover:underline" style={{ color: portal.colore }}>
          Apri portale <ExternalLink size={11} />
        </a>
      </div>
    </div>
  </div>
);

export const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [richiesteInAttesa, setRichiesteInAttesa] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [portals, setPortals] = useState([]);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waStatus, setWaStatus] = useState({ isReady: false });
  const [waChats, setWaChats] = useState([]);
  const [weekApps, setWeekApps] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [form, setForm] = useState({ nome: '', url: '', logo_url: '', colore: '#3b82f6' });
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMsg, setChatMsg] = useState('');
  const [chatChannel, setChatChannel] = useState(null);
  const [teamExtensions, setTeamExtensions] = useState([]);
  const [callingExt, setCallingExt] = useState(null);
  const [reminderData, setReminderData] = useState(null);
  const [tomorrowApps, setTomorrowApps] = useState([]);
  const [todayAppsWithPhone, setTodayAppsWithPhone] = useState([]);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  // Widget preferences
  const [widgetPrefs, setWidgetPrefs] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const isWidgetOn = (id) => widgetPrefs?.widgets?.[id]?.enabled !== false;

  const toggleWidget = async (id) => {
    const updated = { ...widgetPrefs };
    if (!updated.widgets) updated.widgets = {};
    if (!updated.widgets[id]) updated.widgets[id] = { enabled: true, label: WIDGET_META[id]?.label || id };
    updated.widgets[id].enabled = !updated.widgets[id].enabled;
    setWidgetPrefs(updated);
    try { await api.put('/dashboard/preferences', { widgets: updated.widgets }); } catch {}
  };

  // Load preferences
  useEffect(() => {
    api.get('/dashboard/preferences').then(r => setWidgetPrefs(r.data)).catch(() => {
      setWidgetPrefs({ widgets: Object.fromEntries(Object.keys(WIDGET_META).map(k => [k, { enabled: true, label: WIDGET_META[k].label }])) });
    });
  }, []);

  useEffect(() => {
    api.get('/centralino/extensions').then(r => setTeamExtensions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    const fetchLiveData = () => {
      api.get('/notifications').then(r => setRecentNotifs(r.data.slice(0, 3))).catch(() => {});
      api.get('/whatsapp/status').then(r => setWaStatus(r.data)).catch(() => {});
      api.get('/whatsapp/chats').then(r => setWaChats((r.data.chats || []).slice(0, 4))).catch(() => {});
    };
    fetchLiveData();
    const iv = setInterval(fetchLiveData, 30000);
    return () => clearInterval(iv);
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, tasksRes, compRes, portalsRes, calRes, richiesteRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/tasks?status=da_fare'),
        api.get('/compagnie'),
        api.get('/portals').catch(() => ({ data: [] })),
        api.get('/calendario').catch(() => ({ data: [] })),
        api.get('/richieste-emissione').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data.slice(0, 4));
      setCompanies(compRes.data);
      setPortals(portalsRes.data);
      setRichiesteInAttesa((richiesteRes.data || []).filter(r => r.stato === 'in_attesa').slice(0, 5));
      setWeekApps(calRes.data || []);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      setTomorrowApps((calRes.data || []).filter(a => a.data === tomorrowStr && a.telefono));
      const todayStr = new Date().toISOString().split('T')[0];
      setTodayAppsWithPhone((calRes.data || []).filter(a => a.data === todayStr && a.telefono));
      try {
        const notifsRes = await api.get('/notifications');
        setRecentNotifs(notifsRes.data.slice(0, 3));
      } catch {}
      api.get('/gmail/emails').then(r => setRecentEmails((r.data.emails || []).slice(0, 4))).catch(() => {});
      api.get('/reminders/status').then(r => setReminderData(r.data)).catch(() => {});
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/chat/channels').then(res => {
      const channels = res.data || [];
      const general = channels.find(c => c.nome === 'Generale') || channels[0];
      if (general) {
        setChatChannel(general);
        api.get(`/chat/channels/${general.id}/messages`).then(r => setChatMessages((r.data || []).slice(-8))).catch(() => {});
      }
    }).catch(() => {});
    const chatIv = setInterval(() => {
      if (chatChannel) api.get(`/chat/channels/${chatChannel.id}/messages`).then(r => setChatMessages((r.data || []).slice(-8))).catch(() => {});
    }, 15000);
    return () => clearInterval(chatIv);
  }, [chatChannel?.id]);

  const sendChatMsg = async () => {
    if (!chatMsg.trim() || !chatChannel) return;
    try {
      await api.post(`/chat/channels/${chatChannel.id}/messages`, { contenuto: chatMsg });
      setChatMsg('');
      const r = await api.get(`/chat/channels/${chatChannel.id}/messages`);
      setChatMessages((r.data || []).slice(-8));
    } catch { toast.error('Errore invio messaggio'); }
  };

  const callExtension = async (ext) => {
    setCallingExt(ext.internal_number);
    try {
      const res = await api.post('/centralino/call', { number: ext.internal_number, description: ext.description });
      toast.success(res.data.message || 'Chiamata avviata!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore chiamata'); }
    finally { setCallingExt(null); }
  };

  const sendManualReminder = async (app) => {
    setSendingReminderId(app.id);
    try {
      const res = await api.post(`/reminders/send-manual/${app.id}`);
      toast.success(res.data.message || 'Reminder inviato!');
      api.get('/reminders/status').then(r => setReminderData(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio reminder'); }
    finally { setSendingReminderId(null); }
  };

  const handleAddCompany = async () => {
    if (!form.nome || !form.url) { toast.error('Nome e URL obbligatori'); return; }
    try {
      const url = form.url.startsWith('http') ? form.url : `https://${form.url}`;
      await api.post(`/compagnie?nome=${encodeURIComponent(form.nome)}&url=${encodeURIComponent(url)}&logo_url=${encodeURIComponent(form.logo_url)}&colore=${encodeURIComponent(form.colore)}`);
      toast.success('Compagnia aggiunta!');
      setForm({ nome: '', url: '', logo_url: '', colore: '#3b82f6' }); setAddOpen(false); fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleUpdateCompany = async () => {
    if (!editCompany) return;
    try {
      const params = new URLSearchParams();
      if (form.nome) params.set('nome', form.nome);
      if (form.url) params.set('url', form.url.startsWith('http') ? form.url : `https://${form.url}`);
      if (form.logo_url !== undefined) params.set('logo_url', form.logo_url);
      if (form.colore) params.set('colore', form.colore);
      await api.patch(`/compagnie/${editCompany.id}?${params.toString()}`);
      toast.success('Aggiornata!'); setEditCompany(null); fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('Eliminare questa compagnia?')) return;
    try { await api.delete(`/compagnie/${id}`); toast.success('Eliminata'); fetchAll(); }
    catch { toast.error('Errore'); }
  };

  const openEdit = (c) => {
    setEditCompany(c);
    setForm({ nome: c.nome, url: c.url, logo_url: c.logo_url || '', colore: c.colore || '#3b82f6' });
  };

  if (loading || !widgetPrefs) return <div className="flex items-center justify-center h-screen"><div className="h-12 w-12 border-b-2 border-blue-600 rounded-full animate-spin" /></div>;

  const statCards = [
    { icon: CheckSquare, label: 'Task Oggi', value: stats?.task_oggi || 0, color: 'bg-slate-100 text-slate-500', num: 'text-slate-900' },
    { icon: CheckCircle2, label: 'Completati', value: stats?.task_completati || 0, color: 'bg-emerald-50 text-emerald-600', num: 'text-emerald-600' },
    { icon: Clock, label: 'In Corso', value: stats?.task_in_corso || 0, color: 'bg-amber-50 text-amber-600', num: 'text-amber-600' },
    { icon: Users, label: 'Dipendenti', value: stats?.dipendenti_online || 0, color: 'bg-slate-100 text-slate-500', num: 'text-slate-900' },
  ];

  const CompanyFormContent = (
    <div className="space-y-3 pt-2">
      <div><Label className="text-xs">Nome Compagnia</Label><Input value={form.nome} onChange={(e) => setForm(prev => ({...prev, nome: e.target.value}))} placeholder="Es. Allianz" data-testid="company-nome-input" /></div>
      <div><Label className="text-xs">URL Portale</Label><Input value={form.url} onChange={(e) => setForm(prev => ({...prev, url: e.target.value}))} placeholder="https://..." data-testid="company-url-input" /></div>
      <div><Label className="text-xs">URL Logo (opzionale)</Label><Input value={form.logo_url} onChange={(e) => setForm(prev => ({...prev, logo_url: e.target.value}))} placeholder="https://logo.png" /></div>
      <div><Label className="text-xs">Colore</Label><input type="color" value={form.colore} onChange={(e) => setForm(prev => ({...prev, colore: e.target.value}))} className="w-10 h-8 rounded border cursor-pointer" /></div>
    </div>
  );

  const enabledCount = Object.values(widgetPrefs.widgets || {}).filter(w => w.enabled !== false).length;

  return (
    <div className="p-4 sm:p-5 md:p-7 min-h-screen" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Ciao, {user?.nome}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin ? 'Panoramica generale del team' : 'Le tue attivita\' di oggi'}
          </p>
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 transition-colors text-xs font-medium"
          data-testid="dashboard-settings-btn"
        >
          <Settings size={14} />
          <span className="hidden sm:inline">Personalizza</span>
        </button>
      </div>

      {/* Stats Cards */}
      {isWidgetOn('stats') && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="widget-stats">
          {statCards.map((s, i) => (
            <div key={i} data-testid={`stat-card-${i}`} className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.color}`}><s.icon size={18} /></div>
                <div>
                  <p className={`text-xl font-bold ${s.num}`}>{s.value}</p>
                  <p className="text-[11px] text-slate-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Companies */}
      {isWidgetOn('compagnie') && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 mb-5" data-testid="widget-compagnie">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe size={18} className="text-blue-500" />
              <h2 className="text-sm font-bold text-slate-900">Compagnie Assicurative</h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {companies.map(c => (
              <CompanyCard key={c.id} company={c} isAdmin={isAdmin} onEdit={openEdit} onDelete={handleDeleteCompany} />
            ))}
            {isAdmin && (
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <button data-testid="add-company-btn"
                    className="group bg-white rounded-xl border border-dashed border-slate-300 p-4 hover:border-blue-300 hover:bg-blue-50/40 transition-all duration-200 flex flex-col items-center justify-center text-center">
                    <div className="h-12 w-12 rounded-lg flex items-center justify-center mb-2 bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                      <Plus size={20} />
                    </div>
                    <p className="text-xs font-semibold text-slate-500 group-hover:text-blue-600 transition-colors leading-tight">Aggiungi</p>
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Aggiungi Compagnia</DialogTitle></DialogHeader>
                  {CompanyFormContent}
                  <Button onClick={handleAddCompany} className="mt-3 w-full" data-testid="save-company-btn">Salva</Button>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      )}

      {/* Interni Ufficio */}
      {isWidgetOn('interni') && teamExtensions.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 mb-5" data-testid="widget-interni">
          <div className="flex items-center gap-2 mb-4">
            <PhoneCall size={18} className="text-blue-500" />
            <h2 className="text-sm font-bold text-slate-900">Interni Ufficio</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
            {teamExtensions.map(ext => (
              <button key={ext.internal_number} onClick={() => callExtension(ext)} disabled={callingExt === ext.internal_number}
                className="flex items-center gap-3 px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors disabled:opacity-50 w-full"
                data-testid={`call-ext-${ext.internal_number}`}>
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 text-xs font-bold flex-shrink-0">
                  {ext.description?.slice(0, 2).toUpperCase()}
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{ext.description}</p>
                  <p className="text-[10px] text-slate-400">Int. {ext.internal_number}</p>
                </div>
                {callingExt === ext.internal_number ? <Loader2 size={16} className="text-blue-600 animate-spin flex-shrink-0" /> : <PhoneCall size={16} className="text-blue-600 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Portals */}
      {isWidgetOn('portali') && portals.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5" data-testid="widget-portali">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={18} className="text-slate-500" />
            <h2 className="text-sm font-bold text-slate-900">Portali Esterni</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {portals.map(p => <PortalCard key={p.id} portal={p} />)}
          </div>
        </div>
      )}

      {/* Task completions */}
      {isWidgetOn('task_completati') && recentNotifs.length > 0 && (
        <div className="mb-5 space-y-2" data-testid="widget-task-completati">
          {recentNotifs.map(n => {
            const isCompletamento = n.tipo === 'task_completed';
            return (
              <div key={n.id} className={`rounded-lg px-4 py-2.5 flex items-center gap-3 border ${isCompletamento ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
                {isCompletamento ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" /> : <Bell size={16} className="text-slate-400 flex-shrink-0" />}
                <p className={`text-sm flex-1 ${isCompletamento ? 'text-emerald-800' : 'text-slate-700'}`}>{n.messaggio}</p>
                <span className={`text-[10px] ${isCompletamento ? 'text-emerald-500' : 'text-slate-400'}`}>
                  {n.created_at ? format(new Date(n.created_at), 'HH:mm', { locale: it }) : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Agenda */}
          {isWidgetOn('agenda') && (() => {
            const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
            const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
            return (
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-agenda">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CalendarDays size={18} className="text-indigo-500" />
                    <h2 className="text-sm font-bold text-slate-900">Agenda Settimanale</h2>
                    <Badge variant="outline" className="text-[10px] h-5">
                      {format(weekDays[0], 'dd MMM', { locale: it })} - {format(weekDays[6], 'dd MMM', { locale: it })}
                    </Badge>
                  </div>
                  <button onClick={() => navigate('/calendario')} className="text-[11px] text-indigo-500 hover:text-indigo-600 font-medium flex items-center gap-1" data-testid="go-calendario">
                    Apri Calendario <ArrowRight size={12} />
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-1.5">
                  {weekDays.map(day => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const dayApps = weekApps.filter(a => a.data === dateStr).sort((a, b) => (a.ora || '').localeCompare(b.ora || ''));
                    const today = isToday(day);
                    return (
                      <div key={dateStr}
                        className={`rounded-lg p-2 min-h-[100px] transition-colors cursor-pointer ${today ? 'bg-indigo-50 border-2 border-indigo-300' : 'bg-slate-50 border border-slate-100 hover:border-slate-200'}`}
                        onClick={() => navigate('/calendario')} data-testid={`week-day-${dateStr}`}>
                        <div className="text-center mb-1.5">
                          <p className={`text-[10px] font-semibold uppercase ${today ? 'text-indigo-500' : 'text-slate-400'}`}>{format(day, 'EEE', { locale: it })}</p>
                          <p className={`text-lg font-bold leading-tight ${today ? 'text-indigo-700' : 'text-slate-700'}`}>{format(day, 'd')}</p>
                        </div>
                        <div className="space-y-1">
                          {dayApps.slice(0, 3).map(app => (
                            <div key={app.id} className={`rounded px-1.5 py-1 ${today ? 'bg-indigo-100' : 'bg-white border border-slate-100'}`} data-testid={`week-app-${app.id}`}>
                              {app.ora && <p className="text-[9px] font-bold text-indigo-500">{app.ora}</p>}
                              <p className="text-[10px] text-slate-700 font-medium truncate leading-tight">{app.titolo}</p>
                              {app.nome_cliente && <p className="text-[9px] text-slate-400 truncate">{app.nome_cliente}</p>}
                            </div>
                          ))}
                          {dayApps.length > 3 && <p className="text-[9px] text-indigo-500 font-medium text-center">+{dayApps.length - 3} altri</p>}
                          {dayApps.length === 0 && <p className="text-[9px] text-slate-300 text-center mt-2">-</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Mini Chat */}
          {isWidgetOn('chat') && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-chat">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MessageCircle size={18} className="text-indigo-500" />
                  <h2 className="text-sm font-bold text-slate-900">Chat Team</h2>
                  {chatChannel && <Badge variant="outline" className="text-[10px] h-5">#{chatChannel.nome}</Badge>}
                </div>
                <button onClick={() => navigate('/chat')} className="text-[11px] text-indigo-500 hover:text-indigo-600 font-medium flex items-center gap-1" data-testid="go-chat">
                  Apri Chat <ArrowRight size={12} />
                </button>
              </div>
              <ScrollArea className="h-[160px] mb-3">
                {chatMessages.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">Nessun messaggio</p>
                ) : (
                  <div className="space-y-2 pr-3">
                    {chatMessages.map((m, i) => (
                      <div key={m.id || i} className={`flex gap-2 ${m.autore_id === user?.id ? 'justify-end' : ''}`}>
                        {m.autore_id !== user?.id && (
                          <div className="h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center text-[9px] font-bold text-indigo-700 flex-shrink-0">
                            {m.autore_nome?.split(' ').map(w => w[0]).join('') || '?'}
                          </div>
                        )}
                        <div className={`max-w-[75%] rounded-lg px-2.5 py-1.5 ${m.autore_id === user?.id ? 'bg-indigo-500 text-white' : 'bg-slate-100'}`}>
                          {m.autore_id !== user?.id && <p className="text-[9px] font-semibold mb-0.5 text-indigo-600">{m.autore_nome}</p>}
                          <p className={`text-xs leading-relaxed ${m.autore_id === user?.id ? 'text-white' : 'text-slate-700'}`}>{m.contenuto}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <div className="flex gap-2">
                <Input value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendChatMsg(); }} placeholder="Scrivi un messaggio..." className="h-8 text-xs" data-testid="mini-chat-input" />
                <Button size="sm" className="h-8 px-3 bg-indigo-500 hover:bg-indigo-600" onClick={sendChatMsg} disabled={!chatMsg.trim()} data-testid="mini-chat-send"><Send size={13} /></Button>
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Email */}
          {isWidgetOn('email') && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-email">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Mail size={18} className="text-blue-500" />
                  <h2 className="text-sm font-bold text-slate-900">Email Recenti</h2>
                  {recentEmails.filter(e => e.unread).length > 0 && (
                    <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-500 text-white text-[9px] font-bold">
                      {recentEmails.filter(e => e.unread).length}
                    </span>
                  )}
                </div>
                <button onClick={() => navigate('/emails')} className="text-[11px] text-blue-500 hover:text-blue-600 font-medium flex items-center gap-1" data-testid="go-emails">Apri <ArrowRight size={12} /></button>
              </div>
              {recentEmails.length === 0 ? (
                <p className="text-xs text-slate-400 py-4 text-center">Nessuna email recente</p>
              ) : (
                <div className="space-y-1.5">
                  {recentEmails.map((e, i) => (
                    <button key={e.id || i} onClick={() => navigate('/emails')} className="w-full text-left p-2.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`email-preview-${i}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        {e.unread && <div className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                        <p className={`text-xs truncate ${e.unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-600'}`}>
                          {e.from?.split('<')[0]?.trim().replace(/"/g, '') || e.from}
                        </p>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate pl-3.5">{e.subject || 'Nessun oggetto'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* WhatsApp */}
          {isWidgetOn('whatsapp') && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-whatsapp">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageCircle size={18} className="text-emerald-500" />
                  <h2 className="text-sm font-bold text-slate-900">WhatsApp</h2>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${waStatus.isReady ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${waStatus.isReady ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                    {waStatus.isReady ? 'Online' : 'Offline'}
                  </span>
                </div>
                <button onClick={() => navigate('/whatsapp')} className="text-[11px] text-emerald-500 hover:text-emerald-600 font-medium flex items-center gap-1" data-testid="go-whatsapp">Apri <ArrowRight size={12} /></button>
              </div>
              {waChats.length === 0 ? (
                <div className="text-center py-4">
                  <MessageCircle size={28} className="text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">{waStatus.isReady ? 'Nessuna chat recente' : 'Collegati a WhatsApp'}</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {waChats.map((c, i) => (
                    <button key={c.id || i} onClick={() => navigate('/whatsapp')} className="w-full text-left p-2.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`wa-chat-preview-${i}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        {c.unreadCount > 0 && <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-emerald-500 text-white text-[9px] font-bold">{c.unreadCount}</span>}
                        <p className={`text-xs truncate ${c.unreadCount > 0 ? 'font-semibold text-slate-900' : 'font-medium text-slate-600'}`}>{c.name || c.id}</p>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate pl-3.5">{c.lastMessage?.body || 'Nessun messaggio'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reminder */}
          {isWidgetOn('reminder') && reminderData && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-reminder">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bell size={18} className="text-amber-500" />
                  <h2 className="text-sm font-bold text-slate-900">Promemoria WA</h2>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${reminderData.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${reminderData.enabled ? 'bg-emerald-500' : 'bg-red-400'}`} />
                    {reminderData.enabled ? 'Attivo' : 'Off'}
                  </span>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    <button onClick={async () => { try { await api.post(`/reminders/toggle?enabled=${!reminderData.enabled}`); const r = await api.get('/reminders/status'); setReminderData(r.data); toast.success(r.data.enabled ? 'Attivati' : 'Disattivati'); } catch { toast.error('Errore'); } }}
                      className={`p-1.5 rounded-lg transition-colors ${reminderData.enabled ? 'hover:bg-red-50 text-slate-400 hover:text-red-500' : 'hover:bg-emerald-50 text-slate-400 hover:text-emerald-500'}`} data-testid="reminder-toggle-btn">
                      {reminderData.enabled ? <BellOff size={14} /> : <Bell size={14} />}
                    </button>
                    <button onClick={async () => { try { await api.post('/reminders/trigger'); const r = await api.get('/reminders/status'); setReminderData(r.data); toast.success('Controllo eseguito'); } catch { toast.error('Errore'); } }}
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" data-testid="reminder-trigger-btn">
                      <RotateCw size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-slate-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-slate-800">{reminderData.stats?.total || 0}</p>
                  <p className="text-[10px] text-slate-400">Totali</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-emerald-600">{reminderData.stats?.sent || 0}</p>
                  <p className="text-[10px] text-emerald-500">Inviati</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-red-500">{reminderData.stats?.failed || 0}</p>
                  <p className="text-[10px] text-red-400">Falliti</p>
                </div>
              </div>
              {todayAppsWithPhone.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold text-slate-500 mb-1.5">Oggi - Invio manuale</p>
                  <div className="space-y-1">
                    {todayAppsWithPhone.slice(0, 4).map(a => (
                      <div key={a.id} className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 border border-blue-100" data-testid={`reminder-today-${a.id}`}>
                        <Clock size={12} className="text-blue-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-slate-700 truncate">{a.nome_cliente || a.titolo}</p>
                          <p className="text-[10px] text-slate-400">{a.ora || '-'} - {a.telefono}</p>
                        </div>
                        <button onClick={() => sendManualReminder(a)} disabled={sendingReminderId === a.id}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-medium transition-colors disabled:opacity-50" data-testid={`send-reminder-today-${a.id}`}>
                          {sendingReminderId === a.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Invia
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {tomorrowApps.length > 0 && (
                <div className="mb-3">
                  <p className="text-[11px] font-semibold text-slate-500 mb-1.5">Programmati domani</p>
                  <div className="space-y-1">
                    {tomorrowApps.slice(0, 4).map(a => (
                      <div key={a.id} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100" data-testid={`reminder-scheduled-${a.id}`}>
                        <Clock size={12} className="text-amber-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-slate-700 truncate">{a.nome_cliente || a.titolo}</p>
                          <p className="text-[10px] text-slate-400">{a.ora || '-'}</p>
                        </div>
                        <button onClick={() => sendManualReminder(a)} disabled={sendingReminderId === a.id}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-medium transition-colors disabled:opacity-50" data-testid={`send-reminder-tomorrow-${a.id}`}>
                          {sendingReminderId === a.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Invia
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {reminderData.recent?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-slate-500 mb-1.5">Ultimi invii</p>
                  <div className="space-y-1">
                    {reminderData.recent.slice(0, 5).map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50" data-testid={`reminder-log-${i}`}>
                        {r.status === 'sent' ? <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" /> : <AlertCircle size={12} className="text-red-400 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-slate-700 truncate">{r.nome_cliente}</p>
                          <p className="text-[10px] text-slate-400">{r.tipo === '1_giorno' ? '1g prima' : '3h prima'} - {r.data_appuntamento} {r.ora_appuntamento}</p>
                        </div>
                        <Badge className={`border-0 text-[9px] h-4 ${r.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                          {r.status === 'sent' ? 'OK' : 'Fallito'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tasks */}
      {isWidgetOn('tasks') && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-tasks">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckSquare size={18} className="text-amber-500" />
              <h2 className="text-sm font-bold text-slate-900">Task da Completare</h2>
            </div>
            <button onClick={() => navigate('/tasks')} className="text-[11px] text-blue-500 hover:text-blue-600 font-medium flex items-center gap-1" data-testid="go-tasks">Vedi tutti <ArrowRight size={12} /></button>
          </div>
          {tasks.length === 0 ? (
            <p className="text-xs text-slate-400 py-4 text-center">Nessun task in sospeso</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {tasks.map(t => (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all" data-testid={`task-preview-${t.id}`} onClick={() => navigate('/tasks')}>
                  <div className={`h-2 w-2 rounded-full flex-shrink-0 ${t.priorita === 'alta' || t.priorita === 'urgente' ? 'bg-red-500' : t.priorita === 'media' ? 'bg-amber-500' : 'bg-blue-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{t.titolo}</p>
                    <p className="text-[11px] text-slate-400">{t.scadenza ? `Scad. ${format(new Date(t.scadenza), 'dd/MM', { locale: it })}` : 'Nessuna scadenza'}</p>
                  </div>
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <select value={t.stato} onChange={async (e) => {
                      const newStatus = e.target.value;
                      try { await api.patch(`/tasks/${t.id}`, { stato: newStatus }); setTasks(prev => prev.map(task => task.id === t.id ? { ...task, stato: newStatus } : task)); toast.success('Stato aggiornato'); } catch { toast.error('Errore'); }
                    }} className="text-[10px] h-6 px-1.5 rounded border border-slate-200 bg-white text-slate-600 cursor-pointer hover:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid={`task-status-${t.id}`}>
                      <option value="da_fare">Da Fare</option>
                      <option value="in_corso">In Corso</option>
                      <option value="completato">Completato</option>
                      <option value="in_attesa">In Attesa</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Richieste Emissione In Attesa */}
      {isWidgetOn('richieste') && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="widget-richieste">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-orange-500" />
              <h2 className="text-sm font-bold text-slate-900">Richieste Emissione</h2>
              {richiesteInAttesa.length > 0 && <span className="bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{richiesteInAttesa.length}</span>}
            </div>
            <button onClick={() => navigate('/richieste-emissione')} className="text-[11px] text-blue-500 hover:text-blue-600 font-medium flex items-center gap-1" data-testid="go-richieste">Vedi tutte <ArrowRight size={12} /></button>
          </div>
          {richiesteInAttesa.length === 0 ? (
            <p className="text-xs text-slate-400 py-4 text-center">Nessuna richiesta in attesa</p>
          ) : (
            <div className="space-y-2">
              {richiesteInAttesa.map(r => (
                <div key={r.id} onClick={() => navigate('/richieste-emissione')} className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 border border-orange-100 hover:border-orange-300 cursor-pointer transition-all" data-testid={`richiesta-widget-${r.id}`}>
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Clock size={14} className="text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{r.cliente_nome} {r.cliente_cognome}</p>
                    <p className="text-[11px] text-slate-400">{r.compagnia} · {r.tipo_polizza}{r.premio ? ` · €${r.premio}` : ''} · da {r.collaboratore_nome}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={16} />
              Personalizza Dashboard
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">Scegli quali sezioni visualizzare. Le preferenze vengono salvate automaticamente.</p>
          <div className="space-y-1 pt-2" data-testid="widget-settings-list">
            {Object.entries(WIDGET_META).map(([id, meta]) => {
              const Icon = meta.icon;
              const enabled = widgetPrefs?.widgets?.[id]?.enabled !== false;
              return (
                <div key={id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${enabled ? 'bg-white' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-2.5">
                    <Icon size={15} className={enabled ? meta.color : 'text-slate-300'} />
                    <span className={`text-sm ${enabled ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>{meta.label}</span>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={() => toggleWidget(id)}
                    data-testid={`widget-toggle-${id}`}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <p className="text-[11px] text-slate-400">{enabledCount}/{Object.keys(WIDGET_META).length} widget attivi</p>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setSettingsOpen(false)} data-testid="widget-settings-close">
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={!!editCompany} onOpenChange={(v) => { if (!v) setEditCompany(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica Compagnia</DialogTitle></DialogHeader>
          {CompanyFormContent}
          <Button onClick={handleUpdateCompany} className="mt-3 w-full">Aggiorna</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
